import React from "react";
import { motion } from "framer-motion";
import {
  FlaskConical,
  CheckCircle,
  Factory,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

export default function OurStory() {
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3 },
    },
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  const strengths = [
    {
      title: "Proven Expertise",
      text: "Specialized in APIs, drug intermediates, and specialty chemicals manufacturing.",
      icon: FlaskConical,
    },
    {
      title: "Quality Compliance",
      text: "Strict adherence to WHO and GMP global quality standards.",
      icon: CheckCircle,
    },
    {
      title: "Strategic Facilities",
      text: "Dual plants enabling enhanced production and global distribution.",
      icon: Factory,
    },
    {
      title: "Ethical Practices",
      text: "Transparent, customer-focused, and sustainability-driven operations.",
      icon: ShieldCheck,
    },
    {
      title: "Future-Ready",
      text: "Advanced infrastructure with scalability to support long-term growth.",
      icon: TrendingUp,
    },
  ];

  return (
    <div>
      {/* Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/ourstory.webp')",
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/30 to-transparent md:from-black/70 md:via-black/40"></div>

        {/* Hero Title (static, no motion) */}
        <h1 className="relative z-10 text-4xl md:text-5xl font-bold text-white text-center px-4">
          Our Story
        </h1>
      </div>

      {/* Main Section */}
      <motion.section
        id="our-story"
        className="max-w-7xl mx-auto px-6 md:px-8 py-12 md:py-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={container}
      >
        {/* Heading */}
        <motion.h2
          className="text-3xl md:text-4xl font-extrabold text-blue-900 mb-10 md:mb-12 text-center md:text-left"
          variants={fadeInUp}
        >
        </motion.h2>

        {/* Story Content */}
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-start">
          <motion.div
            className="lg:w-1/2 rounded-xl overflow-hidden shadow-lg"
            variants={fadeInUp}
          >
            <img
              src="/images/ourstory-plant.webp"
              alt="MAC Labs manufacturing plant"
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </motion.div>

          <motion.div
            className="lg:w-1/2 space-y-4 md:space-y-6 text-gray-800"
            variants={fadeInUp}
          >
            <p>
              MAC LABS LIMITED began its journey in 2009 as AVIS LABS and has
              grown into a trusted, globally recognized manufacturer. With our
              state-of-the-art main plant at KIADB, Kolhar Industrial Area,
              Bidar, and an additional facility at Bollaram Industrial Area, we
              are strategically positioned to serve domestic and international
              markets efficiently.
            </p>
            <p>
              We specialize in manufacturing Active Pharmaceutical Ingredients
              (APIs), drug intermediates, pharmaceutical formulations,
              cosmetics, and specialty chemicals. Our mission is to deliver
              world-class products while upholding the highest standards of
              quality, ethics, and innovation.
            </p>
            <p>
              For over a decade, MAC LABS LIMITED stands for quality assurance,
              ethical operations, and reliable service. Our robust Quality
              Management System (QMS) and Standard Operating Procedures (SOPs)
              ensure every product meets stringent WHO and GMP global standards.
            </p>
            <p>
              Our strategic locations in Karnataka and Telangana provide a
              competitive edge — enabling faster delivery, stronger supply chain
              resilience, and proximity to major pharmaceutical hubs, supporting
              long-term collaborations.
            </p>
          </motion.div>
        </div>

        {/* Core Strengths */}
        <motion.h3
          id="core-strengths"
          className="text-2xl md:text-3xl font-semibold text-blue-900 mt-14 md:mt-16 mb-6 md:mb-8 text-center md:text-left"
          variants={fadeInUp}
        >
          Core Strengths
        </motion.h3>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          variants={container}
        >
          {strengths.map(({ title, text, icon: Icon }, i) => (
            <motion.div
              key={i}
              className="bg-white p-6 rounded-xl shadow-md border border-gray-200 cursor-pointer"
              whileHover={{
                scale: 1.05,
                boxShadow: "0 12px 30px rgba(13,71,161,0.25)",
                borderColor: "#0d47a1",
                background: "linear-gradient(135deg, #ffffff, #e3f2fd)",
              }}
              variants={fadeInUp}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-blue-700 text-white p-3 rounded-full">
                  <Icon className="w-7 h-7" />
                </div>
                <h4 className="text-lg md:text-xl font-bold text-blue-800">
                  {title}
                </h4>
              </div>
              <p className="text-gray-600 text-sm md:text-base">{text}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Closing Note */}
        <motion.p
          className="text-gray-700 text-center mt-12 md:mt-16 max-w-2xl mx-auto"
          variants={fadeInUp}
        >
          At MAC LABS LTD, we believe success is built on trust, quality, and
          mutual growth. We value our clients as partners, nurturing long-lasting
          relationships based on transparency, reliability, and shared success.
          We deliver confidence, consistency, and collaboration in every
          partnership.
        </motion.p>
      </motion.section>
    </div>
  );
}
